import { cx } from '@emotion/css';
import type { FC, ComponentProps, ReactNode } from 'react';
import { getThemeColor, contrasting, lightenBy } from '../../utils/color-utils';
import type { IconName } from '../Icon';
import Icon from '../Icon';

type AriaRole = 'alert' | 'complementary' | 'note' | 'status';

interface Props extends Omit<ComponentProps<'div'>, 'title'> {
  /** What type of alert is it? */
  color?:
    | 'border'
    | 'danger'
    | 'help'
    | 'info'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'white'
    | string;
  /** Hide the default icon */
  hideIcon?: boolean;
  /** Hide or show the alert */
  isOpen?: boolean;
  /** Override the default ARIA role for the alert type */
  role?: AriaRole;
  /** Add a title to the alert */
  title?: ReactNode;
}

const ODSAlert: FC<Props> = ({
  children,
  className,
  color = 'info',
  hideIcon,
  isOpen = true,
  role: roleProp,
  title,
  ...componentProps
}) => {
  if (!isOpen) {
    return null;
  }

  const colorActual = getThemeColor(color) ?? color;
  const bgColor = lightenBy(colorActual, 0.8);
  const textColor = contrasting(bgColor);

  let icon: IconName | undefined;
  let role: AriaRole = 'note';

  switch (color) {
    case 'danger':
      icon = 'hexagon-exclamation';
      role = 'alert';
      break;
    case 'help':
      icon = 'circle-question';
      role = 'complementary';
      break;
    case 'info':
      icon = 'circle-info';
      role = 'complementary';
      break;
    case 'success':
      icon = 'check-circle';
      role = 'status';
      break;
    case 'warning':
      icon = 'triangle-exclamation';
      role = 'alert';
      break;
    default:
  }

  const iconComponent = icon && !hideIcon ? <Icon name={icon} className="me-2" /> : null;
  const titleComponent = title ? <span className="fw-medium">{title}</span> : null;

  return (
    <div
      className={cx('ods-alert rounded-1', className)}
      css={{
        color: textColor.toString(),
        backgroundColor: bgColor.toString(),
        padding: '0.75em 1em',
      }}
      role={roleProp ?? role}
      {...componentProps}
    >
      {titleComponent ? (
        <div>
          {iconComponent}
          {titleComponent}
        </div>
      ) : (
        iconComponent
      )}
      {children}
    </div>
  );
};

export default ODSAlert;
